import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import { Button } from "@av/components/atoms/Button";
import type { AvE2EProps } from "@av/components/types";
import { MiddleEllipsisFileName } from "./MiddleEllipsisFileName";

const useStyles = createStyles(({ css, token }) => ({
  row: css`
    width: 100%;
    min-width: 0;
  `,
  fileDetails: css`
    min-width: 0;
    flex: 1 1 auto;
  `,
  fileName: css`
    min-width: 0;
    max-width: 100%;
    flex: 0 1 auto;
  `,
  paperClipIcon: css`
    flex: 0 0 auto;
    font-size: 16px;
    line-height: 1;
    color: ${token.colorPrimary};
  `,
  actions: css`
    flex: 0 0 auto;
  `,
  iconButton: css`
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
  `,
  reuploadButton: css`
    color: ${token.colorPrimary};
    background: ${token.colorPrimaryBg};
  `,
  deleteButton: css`
    color: ${token.colorText};
  `,
}));

type DocumentFieldProps = AvE2EProps & {
  file?: UploadFileExtended;
  onDelete: () => void;
  onUploadClick: () => void;
};

export function DocumentUploadField({
  file,
  onDelete,
  onUploadClick,
  testIdPrefix,
}: DocumentFieldProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <div>
      {file ? (
        <Flex className={styles.row} gap={8} align="center">
          <PaperClipOutlined className={styles.paperClipIcon} />
          <Flex className={styles.fileDetails} gap={30} align="center">
            <Flex className={styles.fileName}>
              <MiddleEllipsisFileName
                fileName={file.name}
                testIdPrefix={`${testIdPrefix}-file-name`}
              />
            </Flex>
            <Flex className={styles.actions} align="center" gap={4}>
              <Button
                testIdPrefix={`${testIdPrefix}-swap`}
                type="text"
                icon={<ReloadOutlined />}
                className={`${styles.iconButton} ${styles.reuploadButton}`}
                aria-label={t("recruitment.jobApplication.documents_replaceDocument", {
                  fileName: file.name,
                })}
                onClick={onUploadClick}
              />
              <Button
                testIdPrefix={`${testIdPrefix}-delete`}
                type="text"
                icon={<DeleteOutlined />}
                className={`${styles.iconButton} ${styles.deleteButton}`}
                aria-label={t("recruitment.jobApplication.documents_removeDocument", {
                  fileName: file.name,
                })}
                onClick={onDelete}
              />
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Button
          icon={<UploadOutlined />}
          size="middle"
          testIdPrefix={`${testIdPrefix}-upload`}
          onClick={onUploadClick}
        >
          {t("recruitment.jobApplication.addButton")}
        </Button>
      )}
    </div>
  );
}
